/**
 * Stage 2: Guest Info / Account Creation
 *
 * Handles guest information collection and account creation for anonymous users.
 * If user is already authenticated, displays their info and allows editing.
 * If user is anonymous, shows the signup form with Google OAuth option.
 *
 * Location: src/components/booking/flow/stages/Stage2GuestInfo.jsx
 * Epic: MVP 1.4 - Customer Booking Flow
 */

import { useState, useEffect } from 'react';
import { Eye, EyeOff } from 'lucide-react';
import { useAuth } from '../../../../contexts/AuthContext';
import SocialAuthButtons from '../../../auth/SocialAuthButtons';

export default function Stage2GuestInfo({ bookingData, onNext, onBack, trackProgressAction }) {
  const { user, register, loginWithGoogle: _loginWithGoogle } = useAuth();

  const [formData, setFormData] = useState({
    firstName: bookingData?.customerFirstName || bookingData?.firstName || user?.first_name || '',
    lastName: bookingData?.customerLastName || bookingData?.lastName || user?.last_name || '',
    email: bookingData?.customerEmail || bookingData?.email || user?.email || '',
    password: '',
    confirmPassword: '',
  });

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  // Always allow editing in the booking flow - even if user is logged in, they might want to book for someone else
  const [isExistingUser, setIsExistingUser] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // Pre-fill form if user is authenticated OR from bookingData when navigating back
  useEffect(() => {
    // Priority: bookingData (when navigating back) > user (when authenticated)
    const firstName = bookingData?.customerFirstName || bookingData?.firstName || user?.first_name || '';
    const lastName = bookingData?.customerLastName || bookingData?.lastName || user?.last_name || '';
    const email = bookingData?.customerEmail || bookingData?.email || user?.email || '';

    setFormData({
      firstName,
      lastName,
      email,
      password: '',
      confirmPassword: '',
    });

    // In the booking flow, we treat everyone as a new booking
    // Even if user is authenticated, they might be booking for someone else
    // Only skip password fields if user is already logged in and has no booking data yet
    const hasCustomerData = Boolean(
      bookingData?.customerFirstName || bookingData?.firstName ||
      bookingData?.customerLastName || bookingData?.lastName ||
      bookingData?.customerEmail || bookingData?.email
    );

    // Show as "existing user" (skip password) only if user is logged in and we haven't captured booking data yet
    // This means: user came to booking flow already logged in, and this is their first time on stage 2
    setIsExistingUser(Boolean(user && !hasCustomerData));
  }, [user, bookingData]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error for this field
    setErrors(prev => ({ ...prev, [field]: null }));

    // Track progress
    trackProgressAction(2, 'field_completed');
  };

  const validateForm = () => {
    const newErrors = {};

    // First name validation
    if (!formData.firstName.trim()) {
      newErrors.firstName = 'First name is required';
    }

    // Last name validation
    if (!formData.lastName.trim()) {
      newErrors.lastName = 'Last name is required';
    }

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!emailRegex.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    // Password validation (only for new users)
    if (!isExistingUser) {
      if (!formData.password) {
        newErrors.password = 'Password is required';
      } else if (formData.password.length < 8) {
        newErrors.password = 'Password must be at least 8 characters';
      }

      if (formData.password !== formData.confirmPassword) {
        newErrors.confirmPassword = 'Passwords do not match';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle Google OAuth success
  const handleGoogleSuccess = async (response) => {
    try {
      // User is now logged in via Google OAuth
      // AuthContext has been updated with user info
      const currentUser = response.user;

      onNext({
        customerFirstName: currentUser.first_name,
        customerLastName: currentUser.last_name,
        customerEmail: currentUser.email,
        customerId: currentUser.id,
      });
    } catch (error) {
      console.error('Google OAuth error:', error);
      setErrors({ submit: 'Google authentication failed. Please try again.' });
    }
  };

  const handleGoogleError = (error) => {
    console.error('Google OAuth error:', error);
    setErrors({ submit: 'Google authentication was cancelled or failed.' });
  };

  const handleSubmit = async (_e) => {
    _e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      if (isExistingUser) {
        // User is already logged in, just proceed
        onNext({
          customerFirstName: formData.firstName,
          customerLastName: formData.lastName,
          customerEmail: formData.email,
          customerId: user.id,
        });
      } else {
        // Create new account for anonymous user
        await register({
          email: formData.email,
          password: formData.password,
          firstName: formData.firstName,
          lastName: formData.lastName,
          userType: 'customer',
        });

        // After successful registration, user is auto-logged in by AuthContext
        // Proceed to next stage
        onNext({
          customerFirstName: formData.firstName,
          customerLastName: formData.lastName,
          customerEmail: formData.email,
          customerId: user?.id, // Will be set after registration
        });
      }
    } catch (error) {
      // Check if error has field-specific errors from API
      if (error.fields) {
        // API returned field-specific validation errors
        setErrors({
          ...error.fields,
          submit: error.message || error.error || 'Please fix the errors above'
        });
      } else {
        // Generic error
        setErrors({ submit: error.message || error.error || 'An error occurred. Please try again.' });
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <h2 className="text-2xl font-bold mb-2">Guest Information</h2>
      <p className="text-gray-600 mb-6">
        {isExistingUser
          ? 'Please confirm your information'
          : 'Create an account to complete your booking'}
      </p>

      {/* Social Auth Buttons (for new users only) */}
      {!isExistingUser && (
        <SocialAuthButtons
          userType="customer"
          onSuccess={handleGoogleSuccess}
          onError={handleGoogleError}
        />
      )}

      <form onSubmit={handleSubmit} className="space-y-4">
        {/* First Name */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            First Name <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={formData.firstName}
            onChange={(e) => handleInputChange('firstName', e.target.value)}
            className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 ${
              errors.firstName ? 'border-red-500' : 'border-gray-300'
            }`}
          />
          {errors.firstName && (
            <p className="mt-1 text-sm text-red-600">{errors.firstName}</p>
          )}
        </div>

        {/* Last Name */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Last Name <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={formData.lastName}
            onChange={(e) => handleInputChange('lastName', e.target.value)}
            className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 ${
              errors.lastName ? 'border-red-500' : 'border-gray-300'
            }`}
          />
          {errors.lastName && (
            <p className="mt-1 text-sm text-red-600">{errors.lastName}</p>
          )}
        </div>

        {/* Email */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Email Address <span className="text-red-500">*</span>
          </label>
          <input
            type="email"
            value={formData.email}
            onChange={(e) => handleInputChange('email', e.target.value)}
            className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 ${
              errors.email ? 'border-red-500' : 'border-gray-300'
            }`}
          />
          {errors.email && (
            <p className="mt-1 text-sm text-red-600">{errors.email}</p>
          )}
        </div>

        {/* Password fields (only for new users) */}
        {!isExistingUser && (
          <>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Password <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={formData.password}
                  onChange={(e) => handleInputChange('password', e.target.value)}
                  className={`w-full px-4 py-2 pr-10 border rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 ${
                    errors.password ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="Minimum 8 characters"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
              {errors.password && (
                <p className="mt-1 text-sm text-red-600">{errors.password}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Confirm Password <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <input
                  type={showConfirmPassword ? 'text' : 'password'}
                  value={formData.confirmPassword}
                  onChange={(e) => handleInputChange('confirmPassword', e.target.value)}
                  className={`w-full px-4 py-2 pr-10 border rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 ${
                    errors.confirmPassword ? 'border-red-500' : 'border-gray-300'
                  }`}
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
                >
                  {showConfirmPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
              {errors.confirmPassword && (
                <p className="mt-1 text-sm text-red-600">{errors.confirmPassword}</p>
              )}
            </div>
          </>
        )}

        {/* Submit error */}
        {errors.submit && (
          <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-sm text-red-600">{errors.submit}</p>
          </div>
        )}

        {/* Action buttons */}
        <div className="flex gap-4 pt-4">
          <button
            type="button"
            onClick={onBack}
            className="px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
            disabled={loading}
          >
            Back
          </button>
          <button
            type="submit"
            className="flex-1 px-6 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:bg-gray-300"
            disabled={loading}
          >
            {loading ? 'Processing...' : 'Continue'}
          </button>
        </div>
      </form>
    </div>
  );
}
